import { computed, ref } from 'vue';

import { type UserSettings } from '@/types/api/user-settings';

const TIME_MEDIUM_FORMATS: Record<string, string> = {
  TIME_24_HOURS: 'HH:mm',
  TIME_12_HOURS_A: 'hh:mm a'
};

const TIME_LONG_FORMATS: Record<string, string> = {
  TIME_24_HOURS: 'HH:mm:ss',
  TIME_12_HOURS_A: 'hh:mm:ss a'
};

const TIME_LONG_WITH_MS_FORMATS: Record<string, string> = {
  TIME_24_HOURS: 'HH:mm:ss.SSS',
  TIME_12_HOURS_A: 'hh:mm:ss.SSS a'
};

const TIME_SHORT_FORMATS: Record<string, string> = {
  TIME_24_HOURS: 'H:mm',
  TIME_12_HOURS_A: 'h:mm a'
};

const DATE_LONG_FORMATS: Record<string, string> = {
  DATE_L_PERIOD: 'dd. DD.MM.YYYY',
  DATE_L_SLASH: 'dd. DD/MM/YYYY',
  DATE_B_SLASH: 'dd. MM/DD/YYYY',
  DATE_M_DASH: 'YYYY-MM-DD'
};

const DATE_MEDIUM_FORMATS: Record<string, string> = {
  DATE_L_PERIOD: 'DD.MM.YYYY',
  DATE_L_SLASH: 'DD/MM/YYYY',
  DATE_B_SLASH: 'MM/DD/YYYY',
  DATE_M_DASH: 'YYYY-MM-DD'
};

const LENGTH_FORMATS: Record<string, number> = {
  LENGTH_METRIC: 1,
  LENGTH_IMPERIAL: 0.621371192237334
};

// Map the API number-format keys to valid BCP-47 locale tags. The raw keys
// ('DE'/'EN') are not valid locales, so passing them to Intl.NumberFormat
// silently fell back to the runtime default and ignored the user's preference.
const NUMBER_FORMATS: Record<string, string> = {
  DE: 'de-DE',
  EN: 'en-US'
};

const LENGTH_UNITS: Record<string, string> = {
  LENGTH_METRIC: 'km',
  LENGTH_IMPERIAL: 'mi'
};

const WEIGHT_FORMATS: Record<string, number> = {
  WEIGHT_METRIC: 1,
  WEIGHT_IMPERIAL: 2.20462
};

const WEIGHT_UNITS: Record<string, string> = {
  WEIGHT_METRIC: 'kg',
  WEIGHT_IMPERIAL: 'lb'
};

// Module-level singleton state: the current user's raw API settings.
const settings = ref<UserSettings | null>(null);

/**
 * Replace the current user settings (called from useAuth on app init / update,
 * and on logout with `null` to clear).
 */
export const setUserSettings = (newSettings: UserSettings | null) => {
  settings.value = newSettings;
};

/**
 * Typed access to the current user's display/format settings.
 *
 * Replaces the former `$userSettings` global property and the `UserSettings`
 * filter class. Exposes the raw `settings` plus reactive format getters and
 * conversion helpers derived from them.
 */
export function useUserSettings() {
  const dateFormatKey = computed(() => settings.value?.dateFormat ?? null);
  const timeFormatKey = computed(() => settings.value?.timeFormat ?? null);
  const lengthFormatKey = computed(
    () => settings.value?.lengthFormat ?? 'LENGTH_METRIC'
  );
  const numberFormatKey = computed(() => settings.value?.numberFormat ?? 'EN');
  const weightFormatKey = computed(
    () => settings.value?.weightFormat ?? 'WEIGHT_METRIC'
  );

  // Date/time formats have no default: the key stays null until the user
  // picks one, so these resolve to `undefined` (dayjs then uses its own
  // default format) rather than guessing a format for the user.
  const dateLongFormat = computed(() =>
    dateFormatKey.value ? DATE_LONG_FORMATS[dateFormatKey.value] : undefined
  );
  const dateMediumFormat = computed(() =>
    dateFormatKey.value ? DATE_MEDIUM_FORMATS[dateFormatKey.value] : undefined
  );
  const timeLongFormat = computed(() =>
    timeFormatKey.value ? TIME_LONG_FORMATS[timeFormatKey.value] : undefined
  );
  const timeLongWithMsFormat = computed(() =>
    timeFormatKey.value
      ? TIME_LONG_WITH_MS_FORMATS[timeFormatKey.value]
      : undefined
  );
  const timeMediumFormat = computed(() =>
    timeFormatKey.value ? TIME_MEDIUM_FORMATS[timeFormatKey.value] : undefined
  );
  const timeShortFormat = computed(() =>
    timeFormatKey.value ? TIME_SHORT_FORMATS[timeFormatKey.value] : undefined
  );
  // Unit/number keys always carry a metric/EN default, so fall back to it for
  // any unexpected key instead of returning undefined.
  const numberFormat = computed(
    () => NUMBER_FORMATS[numberFormatKey.value] ?? 'en-US'
  );
  const lengthUnit = computed(
    () => LENGTH_UNITS[lengthFormatKey.value] ?? 'km'
  );
  const weightUnit = computed(
    () => WEIGHT_UNITS[weightFormatKey.value] ?? 'kg'
  );

  const lengthFormat = (kilometers: number): number => {
    if (kilometers) {
      return (
        Math.round(
          kilometers * (LENGTH_FORMATS[lengthFormatKey.value] ?? 1) * 100
        ) / 100
      );
    }
    return 0;
  };

  // API distances are meters while lengthFormat works on kilometers; this is
  // the single place where the meters→km division happens.
  const lengthFormatFromMeters = (meters: number): number =>
    lengthFormat(meters / 1000);

  const weightFormat = (kilograms: number): number => {
    if (kilograms) {
      return (
        Math.round(
          kilograms * (WEIGHT_FORMATS[weightFormatKey.value] ?? 1) * 100
        ) / 100
      );
    }
    return 0;
  };

  const applyDecimalPointer = (
    value: number,
    options?: Intl.NumberFormatOptions
  ): string => new Intl.NumberFormat(numberFormat.value, options).format(value);

  return {
    settings,
    dateLongFormat,
    dateMediumFormat,
    timeLongFormat,
    timeLongWithMsFormat,
    timeMediumFormat,
    timeShortFormat,
    numberFormat,
    lengthUnit,
    weightUnit,
    lengthFormat,
    lengthFormatFromMeters,
    weightFormat,
    applyDecimalPointer
  };
}

export default useUserSettings;
